import React from "react";
import Joi from "joi-browser";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { Form, Modal, Button, Badge, Spinner } from "react-bootstrap";
import { RiDeleteBin2Line } from "react-icons/ri";
import { getPonOLTs, deletePonOLT, addPonOLT, pingPonOLT } from "../services/ponOLTService";
import FormComponent from "./common/formComponent";
import FloatingAddButton from "./common/floatingAddButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import PageLoading from "./common/pageLoading";
import { StyledTableCell, StyledTableRow } from "../services/tableStyles";

class PonOLTs extends FormComponent {
  state = {
    data: {
      name: "",
      model: "",
      host: "",
      sshPort: "",
      username: "",
      password: "",
      pingTime: "Unreachable",
    },
    errors: {},
    olts: [],
    nases: [],
    oltToDelete: {},
    showAddModal: false,
    showConfirmModal: false,
    loading: "",
    processing: false,
  };

  constructor() {
    super();
    this.state.loading = true;
  }

  schema = {
    name: Joi.string().required().label("Name"),
    model: Joi.string().required().label("Model"),
    host: Joi.string().required().label("Host/IP"),
    sshPort: Joi.number().min(1).required().label("SSH Port"),
    username: Joi.string().required().label("Username"),
    password: Joi.string().required().label("Password"),
    pingTime: Joi.string().required().label("Ping Time"),
  };

  doSubmit = async () => {
    this.setState({ processing: true });
    const { data: payload, olts: originalOLTs } = this.state;

    try {
      const { data: newOLT } = await addPonOLT(payload);

      const olts = [...originalOLTs];
      olts.unshift(newOLT);
      this.setState({ olts, showAddModal: false });
      toast.success("PON OLT Added");
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
    this.setState({ processing: false });
  };

  handlePing = async (oltToPing) => {
    this.setState({ processing: true });
    toast.info("Ping Started");
    const { olts } = this.state;
    const index = olts.indexOf(oltToPing);

    const { data: olt } = await pingPonOLT(oltToPing._id);
    olts[index] = olt;
    if (olt.pingTime === "Unreachable") {
      toast.error("Host Unreachable");
      this.setState({ processing: false });
    } else {
      toast.success("Ping Successful");
      this.setState({ processing: false });
    }

    this.setState({ olts });
  };

  handleDeleteOLT = async (oltId) => {
    this.setState({ processing: true });
    const { olts: originalOLTs } = this.state;

    const olts = originalOLTs.filter((o) => o._id !== oltId);
    this.setState({ olts, showConfirmModal: false });

    try {
      await deletePonOLT(oltId);
      toast.success("PON OLT Deleted");
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) toast.error(ex.response.data);
      this.setState({ olts: originalOLTs });
    }
    this.setState({ processing: false });
  };

  showConfirmModal = (oltToDelete) => {
    this.setState({ showConfirmModal: true, oltToDelete });
  };

  handleShowAddModal = () => {
    this.setState({ showAddModal: true });
  };

  handleClose = () => {
    this.setState({ showAddModal: false, showConfirmModal: false });
  };

  async populatePonOLTs() {
    try {
      const { data: olts } = await getPonOLTs();
      this.setState({ olts });
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        toast(ex.response.data);
      }
    }
  }

  async componentDidMount() {
    await this.populatePonOLTs();
    this.setState({ loading: false });
  }

  render() {
    const {
      data,
      errors,
      olts,
      oltToDelete,
      showAddModal,
      showConfirmModal,
      loading,
      processing,
    } = this.state;

    return (
      <Paper elevation={3}>
        <div className="card">
          <div className="card-header main-color-bg">
            <div>
              <h5>PON OLTs</h5>
            </div>
          </div>
          <div className="card-body">
            <span className="card-text">
              {loading && <PageLoading loading={loading} />}
              {!loading && olts.length === 0 && <span>No PON OLT to Display</span>}
              {!loading && (
                <FloatingAddButton classes="float-right no-border mt-2 mb-3" onClick={this.handleShowAddModal} />
              )}
              {!loading && olts.length > 0 && (
                <TableContainer component={Paper}>
                  <Table aria-label="customized table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Name</StyledTableCell>
                        <StyledTableCell align="right">Model</StyledTableCell>
                        <StyledTableCell align="right">Host/IP</StyledTableCell>
                        <StyledTableCell align="right">SSH Port</StyledTableCell>
                        <StyledTableCell align="right"></StyledTableCell>
                        <StyledTableCell align="right"></StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {olts.map((olt) => (
                        <StyledTableRow key={olt._id}>
                          <StyledTableCell component="th" scope="row">
                            <Link to={`/ponolts/${olt._id}`}>{olt.name}</Link>
                          </StyledTableCell>
                          <StyledTableCell align="right">{olt.model}</StyledTableCell>
                          <StyledTableCell align="right">
                            <kbd>{olt.host}</kbd>
                          </StyledTableCell>
                          <StyledTableCell align="right">{olt.sshPort}</StyledTableCell>
                          <StyledTableCell align="right">
                            <Button disabled={processing} onClick={() => this.handlePing(olt)} variant="success">
                              {processing && (
                                <Spinner
                                  as="span"
                                  className="mr-2"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                />
                              )}
                              Ping:{" "}
                              <Badge variant={`${olt.pingTime === "Unreachable" ? "danger" : "warning"}`}>
                                {olt.pingTime} {`${olt.pingTime === "Unreachable" ? "" : "ms"}`}
                              </Badge>
                            </Button>
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <Button
                              onClick={() =>
                                this.showConfirmModal({
                                  _id: olt._id,
                                  name: olt.name,
                                })
                              }
                              variant="danger"
                            >
                              <RiDeleteBin2Line />
                            </Button>
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </span>
          </div>
        </div>

        <Modal show={showAddModal} size="lg" onHide={this.handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>Add Pon OLT</Modal.Title>
          </Modal.Header>
          <Form onSubmit={this.handleSubmit}>
            <Modal.Body>
              <Form.Group controlId="name">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  name="name"
                  onChange={this.handleChange}
                  value={data.name}
                  size="sm"
                  type="text"
                  autoFocus
                />
                {errors.name && <span className="text-danger">{errors.name}</span>}
              </Form.Group>

              <Form.Group controlId="model">
                <Form.Label>Model</Form.Label>
                <Form.Control name="model" onChange={this.handleChange} value={data.model} size="sm" type="text" />
                {errors.model && <span className="text-danger">{errors.model}</span>}
              </Form.Group>

              <Form.Group controlId="host">
                <Form.Label>Host/IP</Form.Label>
                <Form.Control name="host" onChange={this.handleChange} value={data.host} size="sm" type="text" />
                {errors.host && <span className="text-danger">{errors.host}</span>}
              </Form.Group>

              <Form.Group controlId="sshPort">
                <Form.Label>SSH Port</Form.Label>
                <Form.Control name="sshPort" onChange={this.handleChange} value={data.sshPort} size="sm" type="text" />
                {errors.sshPort && <span className="text-danger">{errors.sshPort}</span>}
              </Form.Group>

              <Form.Group controlId="username">
                <Form.Label>Username</Form.Label>
                <Form.Control name="username" onChange={this.handleChange} value={data.username} size="sm" type="text" />
                {errors.username && <span className="text-danger">{errors.username}</span>}
              </Form.Group>

              <Form.Group controlId="password">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  name="password"
                  onChange={this.handleChange}
                  value={data.password}
                  size="sm"
                  type="password"
                />
                {errors.password && <span className="text-danger">{errors.password}</span>}
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button onClick={this.handleClose} variant="secondary">
                Close
              </Button>
              <Button disabled={this.validate() || processing} type="submit" variant="primary">
                {processing && (
                  <Spinner as="span" className="mr-2" animation="border" size="sm" role="status" aria-hidden="true" />
                )}
                Add
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={showConfirmModal} size="md" centered onHide={this.handleClose}>
          <Modal.Header closeButton className="bg-danger text-white">
            <Modal.Title>Delete Pon OLT</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to delete the PON OLT: {oltToDelete.name}?</Modal.Body>

          <Modal.Footer>
            <Button onClick={this.handleClose} variant="secondary">
              No
            </Button>
            <Button
              disabled={processing}
              onClick={() => this.handleDeleteOLT(oltToDelete._id)}
              type="submit"
              variant="primary"
            >
              {processing && (
                <Spinner as="span" className="mr-2" animation="border" size="sm" role="status" aria-hidden="true" />
              )}
              Yes
            </Button>
          </Modal.Footer>
        </Modal>
      </Paper>
    );
  }
}

export default PonOLTs;
